import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    ClipboardList,
    CalendarClock,
    LogOut,
    ChevronDown,
    HardHat,
    Ban,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import ZentraLogo from "@/components/ZentraLogo";
import WhatsAppFAB from "@/components/WhatsAppFAB";

const NAV = [
    { to: "/operario", label: "Hoy", icon: LayoutDashboard, end: true },
    { to: "/operario/tareas", label: "Mis tareas", icon: ClipboardList },
    { to: "/operario/horario", label: "Mi horario", icon: CalendarClock },
    { to: "/operario/limitaciones", label: "Limitaciones", icon: Ban },
];

export default function OperatorLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const currentTitle = NAV.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))?.label || "Portal operario";

    return (
        <div className="flex min-h-screen bg-zentra-bg" data-testid="operator-portal-shell">
            <aside className="hidden lg:flex w-60 flex-col text-white sticky top-0 h-screen" style={{ background: "#0d2419" }} data-testid="operator-sidebar">
                <div className="px-5 pt-7 pb-6 border-b border-white/5">
                    <ZentraLogo variant="light" layout="horizontal" size="md" />
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-widest uppercase text-zentra-accent px-2 py-1 rounded-md border border-zentra-accent/30 bg-zentra-accent/10">
                        <HardHat size={10} /> Operario
                    </div>
                </div>
                <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto" data-testid="operator-nav">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) => `zentra-nav-link ${isActive ? "is-active" : ""}`}
                            data-testid={`op-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <item.icon size={17} strokeWidth={1.7} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="px-4 py-4 border-t border-white/5 text-[10px] text-white/50 leading-relaxed">
                    Solo verás las tareas asignadas a ti.
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="bg-white border-b border-zentra-border sticky top-0 z-30" data-testid="operator-topbar">
                    <div className="flex items-center justify-between px-5 py-3 gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="lg:hidden">
                                <ZentraLogo variant="dark" layout="horizontal" size="sm" />
                            </div>
                            <div className="hidden lg:flex flex-col leading-tight">
                                <span className="text-[11px] tracking-widest uppercase text-zentra-muted">Portal operario</span>
                                <h1 className="font-display text-lg font-semibold text-zentra-ink">{currentTitle}</h1>
                            </div>
                        </div>
                        <div className="relative">
                            <button type="button" onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zentra-border bg-white hover:border-zentra-olive transition" data-testid="op-user-button">
                                <div className="w-7 h-7 rounded-md bg-zentra-primary text-white font-display font-semibold flex items-center justify-center text-xs">
                                    {(user?.name || "O").slice(0, 1).toUpperCase()}
                                </div>
                                <div className="hidden md:flex flex-col leading-tight items-start">
                                    <span className="text-xs font-semibold text-zentra-ink truncate max-w-[140px]">{user?.name}</span>
                                    <span className="text-[10px] text-zentra-muted tracking-wider uppercase">{user?.role === "technician" ? "Técnico" : "Limpieza"}</span>
                                </div>
                                <ChevronDown size={14} className="text-zentra-muted" />
                            </button>
                            {menuOpen && (
                                <div className="absolute right-0 top-12 w-56 bg-white border border-zentra-border rounded-lg shadow-lg p-2 z-50" data-testid="op-user-menu">
                                    <div className="px-3 py-2 border-b border-zentra-border mb-1">
                                        <p className="text-sm font-semibold text-zentra-ink">{user?.name}</p>
                                        <p className="text-xs text-zentra-muted">{user?.email}</p>
                                    </div>
                                    <button type="button" onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zentra-ink hover:bg-zentra-bg transition" data-testid="op-logout-button">
                                        <LogOut size={14} /> Cerrar sesión
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <nav className="lg:hidden flex items-center gap-1 px-2 pb-2 overflow-x-auto" data-testid="op-mobile-nav">
                        {NAV.map((item) => (
                            <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition ${isActive ? "bg-zentra-primary text-white" : "text-zentra-ink bg-zentra-bg"}`}>
                                <item.icon size={14} /> {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </header>
                <main className="flex-1 p-4 md:p-6 max-w-[1100px] w-full mx-auto" data-testid="operator-main">
                    {children}
                </main>
            </div>
            <WhatsAppFAB />
        </div>
    );
}
